import type { CotizacionValorDTO, CreateCotizacionValorRequest } from '../model/kartera-api-model'

/** Fila editable con `key` estable para React aunque aún no tenga `id` del API. */
export interface KeyedRow {
    key: string
}

/** Par descripción/valor (coberturas y características de cotizaciones y templates). */
export interface ValueRow extends KeyedRow {
    id: number | null
    descripcion: string
    valor: string
}

let sequence = 0

export const nextRowKey = () => {
    sequence += 1
    return `row-${sequence}`
}

export const newValueRow = (): ValueRow => ({ key: nextRowKey(), id: null, descripcion: '', valor: '' })

/** `keepIds = false` al copiar desde un template para que el API cree registros nuevos. */
export const toValueRows = (items?: CotizacionValorDTO[] | null, keepIds = true): ValueRow[] =>
    [...(items || [])]
        .sort((left, right) => Number(left.orden ?? 0) - Number(right.orden ?? 0))
        .map((item) => ({
            key: nextRowKey(),
            id: keepIds ? item.id : null,
            descripcion: item.descripcion || '',
            valor: item.valor || '',
        }))

export const toValueRequests = (rows: ValueRow[]): CreateCotizacionValorRequest[] =>
    rows
        .filter((row) => row.descripcion.trim() || row.valor.trim())
        .map((row) => ({ id: row.id, descripcion: row.descripcion.trim(), valor: row.valor.trim() }))

export const patchByKey = <T extends KeyedRow>(rows: T[], key: string, patch: Partial<T>): T[] =>
    rows.map((row) => (row.key === key ? { ...row, ...patch } : row))

export const removeByKey = <T extends KeyedRow>(rows: T[], key: string): T[] => rows.filter((row) => row.key !== key)

export const moveByKey = <T extends KeyedRow>(rows: T[], key: string, delta: -1 | 1): T[] => {
    const index = rows.findIndex((row) => row.key === key)
    const target = index + delta
    if (index < 0 || target < 0 || target >= rows.length) return rows
    const next = [...rows]
    const [moved] = next.splice(index, 1)
    next.splice(target, 0, moved)
    return next
}

/** Mueve el renglón de `from` a `to` (arrastrar y soltar). */
export const moveToIndex = <T>(rows: T[], from: number, to: number): T[] => {
    if (from === to || from < 0 || to < 0 || from >= rows.length || to >= rows.length) return rows
    const next = [...rows]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    return next
}

export const hasIncompleteValues = (rows: ValueRow[]) =>
    rows.some((row) => Boolean(row.descripcion.trim()) !== Boolean(row.valor.trim()))
